import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wallet, ArrowDownRight, ArrowUpRight, Plus, Info } from "lucide-react";

const KIND_META = {
  welcome: { label: "Welcome bonus", color: "text-emerald-700" },
  referral_bonus: { label: "Referral bonus", color: "text-emerald-700" },
  topup: { label: "Top-up", color: "text-emerald-700" },
  admin_credit: { label: "Admin credit", color: "text-emerald-700" },
  booking_earn: { label: "Booking cashback", color: "text-emerald-700" },
  booking_pay: { label: "Applied to booking", color: "text-slate-600" },
};

export default function MyWallet() {
  const [data, setData] = useState({ balance: 0, txns: [] });
  const [loading, setLoading] = useState(true);
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState(500);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/wallet").then(({ data }) => setData(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const doTopup = async () => {
    if (!topupAmount || topupAmount <= 0) return toast.error("Enter a valid amount");
    setSubmitting(true);
    try {
      const { data } = await api.post("/wallet/topup", { amount: Number(topupAmount) });
      toast.success(`₹${topupAmount} added to your wallet`);
      setTopupOpen(false);
      setData((d) => ({ ...d, balance: data.balance }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Top-up failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
        <div className="hw-label text-blue-700">Money</div>
        <h1 className="hw-head text-4xl md:text-5xl font-black text-slate-900 mt-3">My Wallet</h1>
        <p className="text-slate-600 mt-2">Use your wallet at checkout to pay for stays instantly.</p>

        {/* Balance card */}
        <div className="mt-8 relative overflow-hidden rounded-md text-white p-6 hw-shadow-pop" style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #1e40af 60%, #059669 100%)" }} data-testid="wallet-balance-card">
          <div className="flex items-center gap-2 text-white/80"><Wallet className="w-4 h-4" /><span className="hw-label">Available balance</span></div>
          <div className="hw-head text-5xl font-black mt-2" data-testid="wallet-balance">
            {loading ? "…" : `₹${data.balance.toLocaleString("en-IN")}`}
          </div>
          <div className="mt-5 flex gap-3">
            <Button onClick={() => setTopupOpen(true)} className="bg-white text-blue-700 hover:bg-slate-100 rounded-sm font-bold" data-testid="wallet-topup-btn">
              <Plus className="w-4 h-4 mr-1" /> Add money
            </Button>
            <div className="text-xs text-white/80 flex items-center gap-1 max-w-xs">
              <Info className="w-3.5 h-3.5" /> Razorpay payment coming soon — top-ups are instant while we activate it.
            </div>
          </div>
        </div>

        {/* Txns */}
        <div className="mt-8">
          <h2 className="hw-head text-2xl font-black text-slate-900">Transactions</h2>
          {loading ? (
            <div className="hw-card p-8 mt-4 text-center"><span className="hw-pulse-dot" /></div>
          ) : data.txns.length === 0 ? (
            <div className="hw-card p-8 mt-4 text-center" data-testid="wallet-txns-empty">
              <p className="text-slate-600">No transactions yet — top up to get started.</p>
            </div>
          ) : (
            <div className="mt-4 space-y-2" data-testid="wallet-txns-list">
              {data.txns.map((t) => {
                const meta = KIND_META[t.kind] || { label: t.kind, color: "text-slate-700" };
                const credit = t.amount > 0;
                return (
                  <div key={t.txn_id} className="hw-card p-4 flex items-center gap-4" data-testid={`wallet-txn-${t.txn_id}`}>
                    <div className={`w-10 h-10 rounded-full grid place-items-center flex-shrink-0 ${credit ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-700"}`}>
                      {credit ? <ArrowDownRight className="w-5 h-5" /> : <ArrowUpRight className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{meta.label}</div>
                      <div className="text-xs text-slate-500 truncate">{t.note}</div>
                    </div>
                    <div className="text-right">
                      <div className={`hw-head font-black ${credit ? "text-emerald-700" : "text-slate-900"}`}>{credit ? "+" : "−"}₹{Math.abs(t.amount).toLocaleString("en-IN")}</div>
                      <div className="text-[11px] text-slate-400">{new Date(t.created_at).toLocaleDateString("en-IN")}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Dialog open={topupOpen} onOpenChange={setTopupOpen}>
        <DialogContent className="rounded-sm" data-testid="wallet-topup-dialog">
          <DialogHeader>
            <DialogTitle className="hw-head font-black text-2xl">Add money to wallet</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">Instantly credit up to ₹5000. Razorpay activation is in progress.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="hw-label">Amount</Label>
            <Input type="number" min={1} max={5000} value={topupAmount} onChange={(e) => setTopupAmount(e.target.value)} className="rounded-sm mt-1" data-testid="wallet-topup-input" />
            <div className="flex gap-2 mt-3">
              {[100, 500, 1000, 2000].map((v) => (
                <button key={v} type="button" onClick={() => setTopupAmount(v)} className={`px-3 py-1 border rounded-sm text-xs font-bold ${Number(topupAmount) === v ? "border-blue-700 bg-blue-50 text-blue-700" : "border-slate-300 text-slate-700 hover:border-slate-500"}`} data-testid={`wallet-topup-quick-${v}`}>
                  ₹{v}
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTopupOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={doTopup} disabled={submitting} className="bg-blue-700 hover:bg-blue-800 text-white rounded-sm font-bold" data-testid="wallet-topup-confirm">
              {submitting ? "…" : `Add ₹${Number(topupAmount || 0).toLocaleString("en-IN")}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
